import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Box } from '@mui/material';
import { MODAL_SHELL_Z_INDEX } from '../styles/configStyles';

export type FullViewportModalShellProps = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  lockBodyScroll?: boolean;
};

const FullViewportModalShell: React.FC<FullViewportModalShellProps> = ({
  open,
  onClose,
  children,
  lockBodyScroll = true,
}) => {
  const pointerDownOnBackdrop = useRef(false);

  const handleBackdropPointerDown = (e: React.PointerEvent) => {
    pointerDownOnBackdrop.current = e.target === e.currentTarget;
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (!pointerDownOnBackdrop.current) return;
    if (e.target !== e.currentTarget) return;
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !lockBodyScroll) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open, lockBodyScroll]);

  if (!open) return null;

  return createPortal(
    <Box
      data-testid="modal-shell-backdrop"
      onPointerDown={handleBackdropPointerDown}
      onClick={handleBackdropClick}
      sx={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        color: '#fff',
        zIndex: MODAL_SHELL_Z_INDEX,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        data-testid="modal-shell-paper"
        onClick={(e) => e.stopPropagation()}
        sx={{ display: 'contents' }}
      >
        {children}
      </Box>
    </Box>,
    document.body,
  );
};

export default FullViewportModalShell;
